import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Supplier, PaymentTranche } from "models/CashflowModel";
import { formatPercentage } from "../../utils";

interface SupplierManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  suppliers: Supplier[];
  onCreateSupplier: (supplier: { name: string; maxOrderAmount: number | null; tariffRate: number | null; paymentTerms: PaymentTranche[] }) => Promise<void>;
  onUpdateSupplier: (id: number, supplier: { name: string; maxOrderAmount: number | null; tariffRate: number | null; paymentTerms: PaymentTranche[] }) => Promise<void>;
  onDeleteSupplier: (id: number) => void;
  isSaving: boolean;
}

type View = "list" | "create" | "edit";

const TRIGGER_TYPE_OPTIONS = [
  { value: "upon_placement", label: "Deposit (upon placement)" },
  { value: "on_etd", label: "On docs (ETD)" },
  { value: "x_days_after_etd", label: "X Days After ETD" },
];

function emptyTranche(order: number): PaymentTranche {
  return {
    trancheOrder: order,
    triggerType: "upon_placement",
    percentage: 0,
    daysAfterEtd: null,
  };
}

export default function SupplierManagementDialog({
  isOpen,
  onClose,
  suppliers,
  onCreateSupplier,
  onUpdateSupplier,
  onDeleteSupplier,
  isSaving,
}: SupplierManagementDialogProps) {
  const [view, setView] = useState<View>("list");
  const [editingSupplierId, setEditingSupplierId] = useState<number | null>(null);
  const [formName, setFormName] = useState("");
  const [formMaxOrderAmount, setFormMaxOrderAmount] = useState<string>("");
  // Typed as a percent (7.5); stored as a fraction (0.075)
  const [formTariffPercent, setFormTariffPercent] = useState<string>("");
  const [formContactName, setFormContactName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formAddress, setFormAddress] = useState("");
  const [formTranches, setFormTranches] = useState<PaymentTranche[]>([emptyTranche(1)]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const resetForm = () => {
    setFormName("");
    setFormMaxOrderAmount("");
    setFormTariffPercent("");
    setFormContactName("");
    setFormEmail("");
    setFormAddress("");
    setFormTranches([emptyTranche(1)]);
    setEditingSupplierId(null);
  };

  const handleStartCreate = () => {
    resetForm();
    setView("create");
  };

  const handleStartEdit = (supplier: Supplier) => {
    setFormName(supplier.name);
    setFormMaxOrderAmount(supplier.maxOrderAmount != null ? String(supplier.maxOrderAmount) : "");
    setFormTariffPercent(supplier.tariffRate != null ? String(Math.round(supplier.tariffRate * 10000) / 100) : "");
    setFormContactName(supplier.contactName || "");
    setFormEmail(supplier.email || "");
    setFormAddress(supplier.address || "");
    setFormTranches(
      supplier.paymentTerms.length > 0
        ? supplier.paymentTerms.map(t => ({ ...t }))
        : [emptyTranche(1)]
    );
    setEditingSupplierId(supplier.id);
    setView("edit");
  };

  const handleBack = () => {
    setView("list");
    resetForm();
  };

  const handleAddTranche = () => {
    setFormTranches(prev => [...prev, emptyTranche(prev.length + 1)]);
  };

  const handleUpdateTranche = (index: number, updates: Partial<PaymentTranche>) => {
    setFormTranches(prev =>
      prev.map((t, i) => (i === index ? { ...t, ...updates } : t))
    );
  };

  const handleRemoveTranche = (index: number) => {
    setFormTranches(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((t, i) => ({ ...t, trancheOrder: i + 1 }));
    });
  };

  const trancheSum = formTranches.reduce((sum, t) => sum + (t.percentage || 0), 0);
  const isTrancheSumValid = Math.abs(trancheSum - 1) < 0.001;
  const isFormValid = formName.trim() !== "" && formTranches.length > 0 && isTrancheSumValid;

  const handleSave = async () => {
    const data = {
      name: formName.trim(),
      maxOrderAmount: formMaxOrderAmount ? parseFloat(formMaxOrderAmount) : null,
      tariffRate: formTariffPercent !== "" && !isNaN(parseFloat(formTariffPercent)) ? Math.round(parseFloat(formTariffPercent) * 100) / 10000 : null,
      contactName: formContactName.trim() || null,
      email: formEmail.trim() || null,
      address: formAddress.trim() || null,
      paymentTerms: formTranches.map(t => ({
        ...t,
        daysAfterEtd: t.triggerType === "x_days_after_etd" ? (t.daysAfterEtd || 0) : null,
      })),
    };

    if (view === "create") {
      await onCreateSupplier(data);
    } else if (editingSupplierId != null) {
      await onUpdateSupplier(editingSupplierId, data);
    }

    setView("list");
    resetForm();
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmId != null) {
      onDeleteSupplier(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  const getTriggerLabel = (t: PaymentTranche) => {
    const opt = TRIGGER_TYPE_OPTIONS.find(o => o.value === t.triggerType);
    let label = opt?.label || t.triggerType;
    if (t.triggerType === "x_days_after_etd" && t.daysAfterEtd != null) {
      label = `ETD + ${t.daysAfterEtd} days`;
    }
    return label;
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {view === "list" ? "Manage Suppliers" : view === "create" ? "Create Supplier" : "Edit Supplier"}
      </DialogTitle>
      <DialogContent>
        {view === "list" ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleStartCreate}
              >
                Add Supplier
              </Button>
            </Box>

            {suppliers.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Max Order Amount</TableCell>
                      <TableCell>Default tariff</TableCell>
                      <TableCell>Payment Terms</TableCell>
                      <TableCell align="center" sx={{ width: 100 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suppliers.map(s => (
                      <TableRow key={s.id}>
                        <TableCell sx={{ fontWeight: 500 }}>{s.name}</TableCell>
                        <TableCell>
                          {s.maxOrderAmount != null ? `$${s.maxOrderAmount.toLocaleString()}` : "-"}
                        </TableCell>
                        <TableCell>
                          {s.tariffRate != null ? `${Math.round(s.tariffRate * 10000) / 100}%` : "-"}
                        </TableCell>
                        <TableCell>
                          {s.paymentTerms.map(t => (
                            <Typography key={t.trancheOrder} variant="body2">
                              {formatPercentage(t.percentage)} {getTriggerLabel(t)}
                            </Typography>
                          ))}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleStartEdit(s)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteConfirmId(s.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No suppliers configured. Click "Add Supplier" to create one.
              </Typography>
            )}

            {/* Delete confirmation inline */}
            {deleteConfirmId != null && (
              <Alert
                severity="warning"
                sx={{ mt: 2 }}
                action={
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button size="small" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                    <Button size="small" color="error" variant="contained" onClick={handleConfirmDelete} disabled={isSaving}>
                      Delete
                    </Button>
                  </Box>
                }
              >
                Delete supplier "{suppliers.find(s => s.id === deleteConfirmId)?.name}"? This cannot be undone.
              </Alert>
            )}
          </>
        ) : (
          // Create/Edit form
          <>
            <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
              Back to Suppliers
            </Button>

            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Supplier Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
                sx={{ width: 250 }}
              />
              <TextField
                size="small"
                label="Max Order Amount"
                type="number"
                value={formMaxOrderAmount}
                onChange={(e) => setFormMaxOrderAmount(e.target.value)}
                placeholder="Optional"
                sx={{ width: 200 }}
              />
              <TextField
                size="small"
                label="Default tariff rate"
                type="number"
                value={formTariffPercent}
                onChange={(e) => setFormTariffPercent(e.target.value)}
                placeholder="e.g. 7.5"
                helperText="Starting rate for this supplier's PO lines; a product's own rate in the SKU master overrides it"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                sx={{ width: 220 }}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Vendor details (printed on PO documents)
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Contact Name"
                value={formContactName}
                onChange={(e) => setFormContactName(e.target.value)}
                placeholder="Optional"
                sx={{ width: 220 }}
              />
              <TextField
                size="small"
                label="Email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="Optional"
                sx={{ width: 250 }}
              />
              <TextField
                size="small"
                label="Address"
                value={formAddress}
                onChange={(e) => setFormAddress(e.target.value)}
                placeholder="Optional — one line per address line"
                multiline
                minRows={2}
                sx={{ width: 320 }}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Payment Tranches
            </Typography>

            {!isTrancheSumValid && formTranches.some(t => t.percentage > 0) && (
              <Alert severity="error" sx={{ mb: 2, fontWeight: 600, '& .MuiAlert-message': { color: 'error.dark' } }}>
                Tranches must sum to 100%. Current total: {(trancheSum * 100).toFixed(1)}%
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 60 }}>#</TableCell>
                    <TableCell>Trigger Type</TableCell>
                    <TableCell sx={{ width: 120 }}>Percentage</TableCell>
                    <TableCell sx={{ width: 120 }}>Days After ETD</TableCell>
                    <TableCell align="center" sx={{ width: 60 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formTranches.map((tranche, index) => (
                    <TableRow key={index}>
                      <TableCell>{tranche.trancheOrder}</TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <Select
                            value={tranche.triggerType}
                            onChange={(e) =>
                              handleUpdateTranche(index, {
                                triggerType: e.target.value as PaymentTranche["triggerType"],
                              })
                            }
                          >
                            {TRIGGER_TYPE_OPTIONS.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={tranche.percentage ? Math.round(tranche.percentage * 100) : ""}
                          onChange={(e) => {
                            const pct = Math.round(parseFloat(e.target.value)) / 100;
                            handleUpdateTranche(index, { percentage: isNaN(pct) ? 0 : pct });
                          }}
                          inputProps={{ min: 0, max: 100, step: 1 }}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        {tranche.triggerType === "x_days_after_etd" ? (
                          <TextField
                            size="small"
                            type="number"
                            value={tranche.daysAfterEtd ?? ""}
                            onChange={(e) => {
                              const days = parseInt(e.target.value, 10);
                              handleUpdateTranche(index, { daysAfterEtd: isNaN(days) ? null : days });
                            }}
                            inputProps={{ min: 0 }}
                            sx={{ width: 90 }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ opacity: 0.4 }}>N/A</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveTranche(index)}
                          disabled={formTranches.length <= 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddTranche}
            >
              Add Tranche
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {view === "list" ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={handleBack}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || !isFormValid}
            >
              {isSaving ? "Saving..." : "Save Supplier"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
